import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { IStorePackage } from 'models/store/i-store-package.interface';

import { addErrorMessageAction } from 'actions/global-actions';

import { queryKeys } from './query-keys';

// Fetch single store package
const fetchStorePackage = async (id: string): Promise<IStorePackage> => {
  const response = await fetch(`/api/v1/trades/packages/${id}/`);
  if (!response.ok) {
    throw new Error(`Package with ID ${id} not found`);
  }
  return response.json();
};

export interface UseStorePackageQueryReturn {
  data: IStorePackage | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export const useStorePackageQuery = (id: string | undefined): UseStorePackageQueryReturn => {
  const dispatch = useDispatch();

  // Query for single store package
  const {
    data = null,
    isLoading: loading,
    error,
    refetch: refresh,
  } = useQuery({
    queryKey: queryKeys.storePackages.detail(id || ''),
    queryFn: () => fetchStorePackage(id!),
    enabled: !!id, // Only run if ID is provided
  });

  // Handle errors
  useEffect(() => {
    if (error) {
      const errorMessage = (error as any)?.response?.data?.message || error?.message || 'Failed to fetch store package';
      dispatch(addErrorMessageAction(errorMessage));
    }
  }, [error, dispatch]);

  return {
    data,
    loading,
    error: error ? String(error) : null,
    refresh,
  };
};
